import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router';

import BADGE_DETAIL_PARAMS from '@/consts/badgeDetailParams';
import type {Badge} from '@/types/loyalty';

const {BADGE_ID, JOURNEY_ID, LEVEL_BADGE_ID} = BADGE_DETAIL_PARAMS;

/**
 * Module-level depth tracker.
 * BadgeDetailScene writes here on every stack change so that closePopup
 * can pop the exact right number of history entries regardless of how deep
 * the user navigated inside the popup.
 */
let currentPopupDepth = 0;
export const setBadgeDetailDepth = (depth: number) => {
    currentPopupDepth = depth;
};

/**
 * URL-driven badge-detail route hook.
 *
 * - `openBadge(badge)` → pushes `?badge=<id>` for single-level or `?journey=<id>` for journey badges.
 *   BadgeDetailScene picks the right view based on `badge.isSingleLevel`.
 * - `closePopup()`    → pops all popup history entries at once (X button)
 *
 * Native back works step by step inside the popup; when there are no more
 * popup entries, the popup closes.
 */
const useBadgeDetailRoute = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const badgeId = params.get(BADGE_ID) ?? params.get(JOURNEY_ID);
    const levelBadgeId = params.get(LEVEL_BADGE_ID);
    const isOpen = badgeId !== null;

    const openBadge = useCallback(
        (badge: Badge, opts?: {replace?: boolean}) => {
            // Prevent re-navigating when the same badge is already shown.
            if (badge.id === badgeId) return;

            const search = new URLSearchParams(location.search);

            if (badge.isSingleLevel) {
                search.set(BADGE_ID, badge.id);
                search.delete(JOURNEY_ID);
            } else {
                search.set(JOURNEY_ID, badge.journeyId ?? badge.id);
                search.delete(BADGE_ID);
            }
            search.delete(LEVEL_BADGE_ID);

            const url = `${location.pathname}?${search.toString()}`;
            if (opts?.replace) {
                navigate(url, {replace: true, state: {animateAsForward: true}});
            } else {
                navigate(url);
            }
        },
        [navigate, location, badgeId]
    );

    const openLevel = useCallback(
        (levelBadgeId: string) => {
            if (!badgeId) return;
            const search = new URLSearchParams(location.search);
            search.set(LEVEL_BADGE_ID, levelBadgeId);
            navigate(`${location.pathname}?${search.toString()}`);
        },
        [navigate, location, badgeId]
    );

    /** Closes the popup entirely, popping all internal navigation history at once. */
    const closePopup = useCallback(() => {
        if (!isOpen) return;
        if (currentPopupDepth > 0) {
            navigate(-currentPopupDepth);
            return;
        }
        // Fallback: strip badge params directly (e.g. before scene has initialised).
        const search = new URLSearchParams(location.search);
        search.delete(BADGE_ID);
        search.delete(JOURNEY_ID);
        search.delete(LEVEL_BADGE_ID);
        navigate(`${location.pathname}?${search.toString()}`, {replace: true});
    }, [navigate, isOpen, location]);

    return {badgeId, levelBadgeId, isOpen, openBadge, openLevel, closePopup};
};

export default useBadgeDetailRoute;
